import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/agents/log-triage/learnings` — admin-only review queue for what the log triage
 * learned from people's own handling of its tickets.
 *
 * A signature whose tickets were deleted, or closed without anyone booking a
 * minute on them, is proposed as a rule. It does nothing until Activated;
 * Dismiss files that signature again from the next sweep on, ten minutes
 * later. See `server/services/log-triage/learning.ts`.
 */

type Learning = {
  id: string;
  signatureHash: string;
  sample: string;
  container: string;
  action: "drop" | "low";
  status: "draft" | "active" | "dismissed";
  evidenceCount: number;
  rationale: string;
  appliedCount: number;
  lastAppliedAt: string | null;
  generatedAt: string;
  project: { id: string; name: string } | null;
  reviewedBy: { id: string; name: string | null } | null;
  reviewedAt: string | null;
};

const TABS = [
  { key: "draft", label: "Proposed" },
  { key: "active", label: "Active" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all", label: "All" },
] as const;
type Tab = (typeof TABS)[number]["key"];

const ACTION_LABEL: Record<Learning["action"], string> = {
  drop: "don't open a ticket",
  low: "open at low priority",
};

export function TriageLearningsPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [mining, setMining] = useState(false);
  const [mineMessage, setMineMessage] = useState<string | null>(null);

  const load = useCallback((t: Tab) => {
    setRows(null);
    return api
      .get<{ learnings: Learning[] }>(
        `/api/log-triage-learnings${t === "all" ? "" : `?status=${t}`}`,
      )
      .then((res) => setRows(res.learnings))
      .catch(() => setError("Couldn't load the rules."));
  }, []);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  async function patch(id: string, body: Record<string, string>, failure: string) {
    setBusyId(id);
    try {
      await api.patch(`/api/log-triage-learnings/${id}`, body);
      await load(tab);
    } catch {
      setError(failure);
    } finally {
      setBusyId(null);
    }
  }

  async function mineNow() {
    setMining(true);
    setMineMessage(null);
    try {
      await api.post("/api/log-triage-learnings/mine", {});
      setMineMessage("Mining is running — switch tabs in a moment to reload the list.");
    } catch {
      setMineMessage("Couldn't start the mining run.");
    } finally {
      setMining(false);
    }
  }

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">What Argus has learned</h2>
          <p className="text-sm text-muted-foreground">
            Signatures whose tickets you deleted or closed without anyone working on them. A rule
            does nothing until you activate it — and dismissing one takes effect immediately: the
            next sweep opens that error again.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void mineNow()}
            disabled={mining}
          >
            {mining ? "Starting…" : "Go through tickets now"}
          </Button>
          {mineMessage && <p className="text-xs text-muted-foreground">{mineMessage}</p>}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`triage-learnings-tab-${t.key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="triage-learnings-empty">
          {tab === "draft"
            ? "Nothing waiting — Argus hasn't found a signature people throw away repeatedly."
            : "Nothing here."}
        </p>
      )}

      <div className="space-y-3">
        {rows?.map((r) => (
          <Card key={r.id} data-testid="triage-learning-card">
            <CardContent className="space-y-2 p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline">{r.project?.name ?? "All projects"}</Badge>
                <span className="font-mono">{r.container}</span>
                <span>· thrown away {r.evidenceCount}×</span>
                <span>· {ACTION_LABEL[r.action]}</span>
                {r.appliedCount > 0 && <span>· applied {r.appliedCount}×</span>}
                {r.reviewedBy && (
                  <span>
                    · {r.status === "active" ? "activated by" : "dismissed by"}{" "}
                    {r.reviewedBy.name ?? "someone"}
                  </span>
                )}
              </div>
              <p className="font-mono text-xs break-all">{r.sample}</p>
              <p className="text-sm">{r.rationale}</p>
              <div className="flex flex-wrap gap-2 pt-1">
                {r.status !== "active" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === r.id}
                    data-testid="activate-triage-learning"
                    onClick={() =>
                      void patch(r.id, { status: "active" }, "Couldn't save the decision.")
                    }
                  >
                    Activate
                  </Button>
                )}
                {r.status !== "dismissed" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyId === r.id}
                    data-testid="dismiss-triage-learning"
                    onClick={() =>
                      void patch(r.id, { status: "dismissed" }, "Couldn't save the decision.")
                    }
                  >
                    Dismiss
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busyId === r.id}
                  onClick={() =>
                    void patch(
                      r.id,
                      { action: r.action === "drop" ? "low" : "drop" },
                      "Couldn't save the change.",
                    )
                  }
                >
                  {r.action === "drop" ? "Low priority instead" : "Don't open at all instead"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
